"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useContext, useState } from "react";
import { FormContext } from "@/contexts/FormDataContext";
import { useSchema } from "@/contexts/FormSchemaContext";
import { GlobalContext } from "@/contexts/SiteDataContext";
import { FieldType, FormStepType } from "@/data/forms/types";
import { useApi } from "./useApi";
import { createOrUpdateApplication } from "@/components/Form/services/createOrUpdateApplication";
import LocalStorageUtils from "@/utils/localStorageUtils";
import postCreateApplicationHandler from "@/interface/api/PostCreateApplication";
import putEditApplicationHandler from "@/interface/api/PutEditApplication";
import getApplicationNameHandler from "@/interface/api/GetApplicationName";
import { getCookieValue, removeCookie, setCookieValue } from "@/utils/cookies";
import { getAddonsWithDiscount } from "@/utils/Form/formatData";
import {
  amplitudeTrackUserFromEmail,
  appendAmplitudeCrossDomainParams,
} from "@/services/Amplitude";

const CONTACT_INFORMATION_STEP = "contact-information";

export const useFormUtils = () => {
  let { formSchema, pdfId } = useSchema();
  formSchema = formSchema || emptyForm;
  const {
    formData,
    setFormData,
    step,
    setStep,
    setCompletedSteps,
    completedSteps,
    validatedForm,
    formDetails,
    setLoadingFromApi,
    setValidatedForm,
    selectedMethod,
    proofOfTravelValue,
    setProofOfTravelError,
    proofOfTravelRef,
    servicesDetails,
  } = useContext(FormContext)!;

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { language, siteData } = useContext(GlobalContext).context;
  const siteAddons =
    typeof siteData === "object" && siteData !== null ? siteData.addons : undefined;

  const requestPostCreateApplication = useApi(
    postCreateApplicationHandler.postCreateApplication
  ).request;
  const requestPutEditApplication = useApi(putEditApplicationHandler.putEditApplication).request;

  const requestGetApplicationName = useApi(getApplicationNameHandler.getApplicationName).request;

  const currentStep = formSchema[step];
  const isLastStep = step === formSchema.length - 1;

  const [isSubmitting, setIsSubmitting] = useState(false);

  const isGroupVisible = ({
    groupName,
    formValues,
    formSteps,
  }: {
    groupName: string;
    formValues: Record<string, any>;
    formSteps: FormStepType[];
  }): boolean => {
    const group = formSteps
      .flatMap((step: any) => step.groups) // Extraemos todos los grupos de todos los pasos
      .find((g: any) => g.name === groupName);
    if (group.condition) {
      const { field: dependentField, value, values } = group.condition;
      if (value !== undefined) return formValues[dependentField] === value;
      if (values) return values.includes(formValues[dependentField]);
    }
    return true; // Si no hay condición, mostramos el campo
  };

  const isFieldVisible = ({
    fieldName,
    formValues,
    formSteps,
  }: {
    fieldName: string;
    formValues: Record<string, any>;
    formSteps: FormStepType[];
  }): boolean => {
    const field = formSteps
      .flatMap((step) => step.groups.flatMap((group) => group.fields))
      .find((f: any) => f.name === fieldName);
    if (field?.condition) {
      const { field: dependentField, value, values } = field.condition;
      if (value !== undefined) return formValues[dependentField] === value;
      if (values) return values.includes(formValues[dependentField]);
    }
    return true; // Si no hay condición, mostramos el campo
  };

  const setRequiredFieldValidation = (field: FieldType) => {
    if (field.name === "emergencyAddress2") return;
    let requiredMessage;

    switch (field.name) {
      case "emergencyZipCode":
        requiredMessage = "Please enter the ZIP code.";
        break;
      case "emergencyAddress1":
        requiredMessage = "Please enter the address.";
        break;
      default:
        requiredMessage = `Please enter the ${field.label.toLowerCase()}.`;
        break;
    }

    return {
      ...field.validation,
      required: requiredMessage,
    };
  };

  const isSubGroupVisible = (subGroup: any, formValues: Record<string, any>) => {
    return (
      !subGroup.condition ||
      (subGroup.condition.value !== undefined &&
        formValues[subGroup.condition.field] === subGroup.condition.value) ||
      (subGroup.condition.values &&
        subGroup.condition.values.includes(formValues[subGroup.condition.field]))
    );
  };

  const setPathnameWithCurrentStep = () => {
    const queryParams = new URLSearchParams(searchParams).toString();

    if (!isSubmitting) {
      router.push(`${pathname}?${queryParams}#${currentStep.name}`);
    }
  };

  const setDefaultFormValues = (setValue: any) => {
    return currentStep.groups.forEach((group: any) => {
      group.fields.forEach((field: any) => {
        setValue(field.name, formData[field.name] ?? null);
      });
    });
  };

  const isEditingForm = pathname.startsWith("/edit") || pathname.startsWith("/es/edit");

  function addUniqueNumber(arr: number[], num: number): number[] {
    if (!arr.includes(num)) {
      arr.push(num);
      arr.sort((a, b) => a - b);
    }
    return arr;
  }

  const saveCompletedStepsLocalAndState = (completedSteps: any, step: any) => {
    const arraytoSave = addUniqueNumber(completedSteps, step);
    setCompletedSteps(arraytoSave);
    LocalStorageUtils.setItem("COMPLETED_STEPS", arraytoSave);
  };

  const syncAmplitudeUserFromContactStep = async (email: string | null | undefined) => {
    if (currentStep?.name !== CONTACT_INFORMATION_STEP) return;
    await amplitudeTrackUserFromEmail(email);
  };

  const onSubmit = async (data: any) => {
    const addons = getAddonsWithDiscount(
      {
        ...data,
        pdfId,
        addons: data.addons ?? formData.addons ?? formDetails?.addons,
      },
      siteAddons
    );
    setFormData((prev: any) => ({ ...prev, ...data, addons }));

    await syncAmplitudeUserFromContactStep(data.emailAddress);

    if (isLastStep || isEditingForm) {
      setIsSubmitting(true);

      try {
        const response = await createOrUpdateApplication({
          applicationValues: { ...data, addons },
          pdfId: pdfId ?? "",
          language,
          formDetails,
          siteAddons,
          requestPostCreateApplication,
          requestPutEditApplication,
          requestGetApplicationName,
        });
        if (!response) {
          throw new Error("Failed to create or update application: response is null or undefined.");
        }
        if (response.status === 201 || response.status === 200) {
          const lastPartOfPathname = pathname.split("/").filter(Boolean).pop();
          router.push(`/review/${lastPartOfPathname}`);
        }
      } catch (error) {
        console.log(error);
        setLoadingFromApi(false);
      }
    } else {
      setStep(step + 1);
    }
  };

  const handleEditGroup = (stepIndex: number) => {
    // Test AB PASGF-1664
    const isTestAB = Boolean(pathname?.includes("/review/") && pathname?.includes("/alt"));
    const pathnameWithoutReview = isTestAB
      ? pathname.replace("/es", "").replace("/review", "").replace("/alt", "")
      : pathname.replace("/es", "").replace("/review", "");

    setStep(stepIndex);
    router.push(`/edit/${pathnameWithoutReview}`);
  };

  // Validate that when the user has selected a service, he has a proof of travel
  const handleContinueWithServiceValidation = () => {
    if (selectedMethod !== "form" && servicesDetails.length > 0) {
      if (!proofOfTravelValue || proofOfTravelValue === "no") {
        setProofOfTravelError(true);
        setTimeout(() => {
          proofOfTravelRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
        return true;
      }
    }

    return false;
  };

  const handleSendReviewedForm = async () => {
    const hasError = handleContinueWithServiceValidation();
    if (hasError) return;

    const formDataToSubmit = { ...formData };
    const formName = formDetails?.pdf?.name;

    setValidatedForm(false);

    let pdfIdToSend: any = pdfId ?? "";
    if (selectedMethod !== "form") {
      const selectedService = servicesDetails.find(
        (service: any) => service.pdf.name === selectedMethod
      );
      pdfIdToSend = selectedService?.pdf?.id ?? "";

      if (pdfIdToSend !== "") {
        const orderCookie = getCookieValue("address_token");
        if (orderCookie) {
          setCookieValue("address_token", { ...orderCookie, pdfId: pdfIdToSend });
        }
      }
    }

    try {
      const response = await createOrUpdateApplication({
        applicationValues: formDataToSubmit,
        pdfId: pdfIdToSend,
        language,
        formDetails,
        siteAddons,
        requestPostCreateApplication,
        requestPutEditApplication,
        requestGetApplicationName,
      });
      if (!response) {
        throw new Error("Failed to update application: response is null or undefined.");
      }
      if (response.status === 201 || response.status === 200) {
        LocalStorageUtils.removeItem(`FORM_DATA_${formDetails.pdf.name}`);
        LocalStorageUtils.removeItem(`FORM_DATA`);
        const orderCookie = getCookieValue("address_token");
        const tagCookie = getCookieValue("tag_param");
        const utmCookie = getCookieValue("utm_params");

        let currentParams = new URLSearchParams(searchParams);
        let redirect = "/payment";

        if (orderCookie.paymentSite !== orderCookie.originalSite) {
          currentParams = new URLSearchParams();
          currentParams.set("order", orderCookie.applicationId);
          currentParams.set("pdf", orderCookie.pdfId);
          currentParams.set("lang", language);

          appendAmplitudeCrossDomainParams(currentParams);

          if (tagCookie?.tag) {
            currentParams.set("tag", tagCookie.tag);
          }

          if (utmCookie) {
            Object.entries(utmCookie).forEach(([key, value]) => {
              currentParams.set(key, value as string);
            });
          }

          const paymentSite = orderCookie.paymentSite.startsWith("https://")
            ? orderCookie.paymentSite
            : `https://${orderCookie.paymentSite}`;

          redirect = `${paymentSite}/payment?${currentParams.toString()}`;
        }

        router.push(redirect);
      }
    } catch (error) {
      console.log(error);
      setValidatedForm(true);
    }
  };

  const redirectToFormIsNotValid = () => {
    if (!validatedForm) {
      const lastPartOfPathname = pathname.split("/").filter(Boolean).pop();
      router.push(`/${lastPartOfPathname}`);
    }
  };

  const areAllPropertiesFalsy = (obj: Record<string, any>): boolean => {
    return Object.values(obj).every((value) => !value);
  };

  const getAllFormStepsFields = (formSteps: any) => {
    let fieldsArray: any[] = [];

    formSteps.forEach((section: any) => {
      section.groups.forEach((group: any) => {
        group.fields.forEach((field: any) => {
          fieldsArray.push(field);
        });
      });
    });

    return fieldsArray;
  };

  const getDefaultValues = (formSteps: any) => {
    const fieldsArray = getAllFormStepsFields(formSteps);
    const defaultValues: Record<string, any> = {};

    fieldsArray.forEach((field: any) => {
      if (field.name) {
        defaultValues[field.name] = field.defaultValue;
      }
    });

    return defaultValues;
  };

  const getStepFields = (currentStep: FormStepType): string[] => {
    if (!currentStep || !currentStep.groups) return [];
    const stepFields: string[] = [];

    currentStep.groups.forEach((group) => {
      if (group.fields && Array.isArray(group.fields)) {
        group.fields.forEach((field) => {
          stepFields.push(field.name);
        });
      }
      if (group.subGroups && Array.isArray(group.subGroups)) {
        group.subGroups.forEach((subGroup: any) => {
          if (subGroup.fields && Array.isArray(subGroup.fields)) {
            subGroup.fields.forEach((field: FieldType) => {
              stepFields.push(field.name);
            });
          }
        });
      }
    });

    return stepFields;
  };

  const triggerStepFields = async (
    stepFields: string[],
    trigger: (name: string | string[]) => Promise<boolean>
  ) => {
    if (stepFields.length === 0) return true;

    return trigger(stepFields);
  };

  const triggerAndFocusFirstError = async (
    stepFields: string[],
    trigger: (name: string | string[]) => Promise<boolean>,
    setFocus: (name: string) => void,
    getFieldState: (name: string) => { invalid?: boolean }
  ) => {
    const isStepValid = await triggerStepFields(stepFields, trigger);

    if (!isStepValid) {
      const firstError = stepFields.find((fieldName) => getFieldState(fieldName).invalid);
      if (firstError) setFocus(firstError);
    }

    return isStepValid;
  };

  const handleBack = async (step: number, setStep: (s: number) => void) => {
    setStep(step - 1);
  };

  const handleNext = async (
    step: number,
    setStep: (s: number) => void,
    setFocus: (name: string) => void,
    stepFields: string[],
    trigger: (name: string | string[]) => Promise<boolean>,
    getFieldState: (name: string) => { invalid?: boolean },
    formValues: Record<string, any>
  ) => {
    !isEditingForm && saveCompletedStepsLocalAndState(completedSteps, step);
    const isStepValid = await triggerAndFocusFirstError(
      stepFields,
      trigger,
      setFocus,
      getFieldState
    );

    if (isStepValid) {
      await syncAmplitudeUserFromContactStep(formValues.emailAddress);
      setStep(step + 1);
    }

    return isStepValid;
  };

  return {
    isGroupVisible,
    isFieldVisible,
    isSubGroupVisible,
    setRequiredFieldValidation,
    setPathnameWithCurrentStep,
    setDefaultFormValues,
    handleBack,
    handleNext,
    onSubmit,
    handleEditGroup,
    handleSendReviewedForm,
    isEditingForm,
    redirectToFormIsNotValid,
    areAllPropertiesFalsy,
    getDefaultValues,
    getStepFields,
    triggerStepFields,
  };
};

const emptyForm: FormStepType[] = [
  {
    id: 1,
    name: "empty-form",
    title: "1. Empty Form",
    groups: [
      {
        name: "",
        title: "",
        fields: [],
      },
    ],
  },
];

export function cleanOptionalKeys(data: Record<string, any>): Record<string, any> {
  const cleaned: Record<string, any> = {};
  Object.entries(data).forEach(([key, value]) => {
    if (key.includes("--optional")) {
      if (value !== "" && value !== null && value !== undefined) {
        const newKey = key.replace("--optional", "");
        cleaned[newKey] = value;
      }
    } else {
      cleaned[key] = value;
    }
  });
  return cleaned;
}

export const cleanAllFormLocalStorage = () => {
  LocalStorageUtils.removeItem("ACTIVE_FORM_NAME");
  LocalStorageUtils.removeItem("COMPLETED_STEPS");
  LocalStorageUtils.removeItem("FORM_DETAILS");
  LocalStorageUtils.removeItem("STEP");
  LocalStorageUtils.removeItem("SELECTED_METHOD");
  LocalStorageUtils.removeItem("SERVICES_DETAILS");
};

export const cleanFormCookies = () => {
  removeCookie("address_token");
  removeCookie("pdfId");
  removeCookie("app_id");
  removeCookie("order_status");
};
